import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { wasteManagementService } from '../services';
import { useAuthStore } from '../store/authStore';

interface WasteRecord {
  id: number;
  trello_card_id: string;
  trello_card_name: string;
  batch_number: string | null;
  source_weight_lbs: number | null;
  source_weight_raw: string | null;
  current_list_name: string | null;
  pfp_in_weight_lbs: number | null;
  after_print_weight_lbs: number | null;
  waste_weight_lbs: number | null;
  locked_pfp_in: boolean;
  locked_after_print: boolean;
  updated_at: string;
}

interface WasteSummaryRow {
  bucket: string;
  total_records: number;
  measured_records: number;
  total_waste_lbs: number;
  avg_waste_lbs: number | null;
}

type StatusFilter = 'all' | 'pfp_open' | 'after_print_open' | 'completed' | 'waste_ready';

const WasteManagementSystem: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician';

  const [tabIndex, setTabIndex] = useState(0);
  const [records, setRecords] = useState<WasteRecord[]>([]);
  const [summaryRows, setSummaryRows] = useState<WasteSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [listFilter, setListFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [summaryPeriod, setSummaryPeriod] = useState<'date' | 'week' | 'month' | 'year'>('date');
  const [summaryStatus, setSummaryStatus] = useState<StatusFilter>('all');
  const [summaryStartDate, setSummaryStartDate] = useState('');
  const [summaryEndDate, setSummaryEndDate] = useState('');

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pfpDraft, setPfpDraft] = useState<Record<number, string>>({});
  const [afterPrintDraft, setAfterPrintDraft] = useState<Record<number, string>>({});

  const getRecordStatus = (record: WasteRecord): StatusFilter => {
    if (!record.locked_pfp_in) return 'pfp_open';
    if (record.locked_pfp_in && !record.locked_after_print) return 'after_print_open';
    if (record.pfp_in_weight_lbs !== null && record.after_print_weight_lbs !== null) return 'waste_ready';
    return 'completed';
  };

  const getStatusChip = (record: WasteRecord) => {
    const status = getRecordStatus(record);
    if (status === 'pfp_open') {
      return <Chip size="small" color="success" label="PFP In Open" />;
    }
    if (status === 'after_print_open') {
      return <Chip size="small" color="info" label="After Print Open" />;
    }
    if (status === 'waste_ready') {
      return <Chip size="small" color="warning" label="Waste Ready" />;
    }
    return <Chip size="small" color="default" label="Completed" />;
  };

  const loadRecords = async (doSync = true) => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        current_list_name: listFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        sync: doSync,
      };
      const data = await wasteManagementService.getAll(params);
      setRecords(data.records || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load waste management records');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setSummaryLoading(true);
      setError(null);
      const params: any = {
        period: summaryPeriod,
        status: summaryStatus === 'all' ? undefined : summaryStatus,
        start_date: summaryStartDate || undefined,
        end_date: summaryEndDate || undefined,
      };
      const data = await wasteManagementService.getSummary(params);
      setSummaryRows(data.summary || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load waste summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(true);
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setMessage(null);
      const result = await wasteManagementService.sync();
      setMessage(`Sync complete: ${result.summary?.created || 0} created, ${result.summary?.updated || 0} updated`);
      await loadRecords(false);
      await loadSummary();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const savePfp = async (record: WasteRecord) => {
    const raw = pfpDraft[record.id];
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) {
      setError('Please enter a valid non-negative PFP In Weight');
      return;
    }

    try {
      await wasteManagementService.updatePfpIn(record.id, value);
      setMessage('PFP In Weight updated successfully');
      await loadRecords(false);
      await loadSummary();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update PFP In Weight');
    }
  };

  const saveAfterPrint = async (record: WasteRecord) => {
    const raw = afterPrintDraft[record.id];
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) {
      setError('Please enter a valid non-negative After Print Weight');
      return;
    }

    try {
      await wasteManagementService.updateAfterPrint(record.id, value);
      setMessage('After Print Weight updated successfully');
      await loadRecords(false);
      await loadSummary();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update After Print Weight');
    }
  };

  const statusCounts = records.reduce(
    (acc, record) => {
      const key = getRecordStatus(record);
      acc.total += 1;
      if (key === 'pfp_open') acc.pfp_open += 1;
      if (key === 'after_print_open') acc.after_print_open += 1;
      if (key === 'completed') acc.completed += 1;
      if (record.pfp_in_weight_lbs !== null && record.after_print_weight_lbs !== null) {
        acc.waste_ready += 1;
      }
      return acc;
    },
    { total: 0, pfp_open: 0, after_print_open: 0, completed: 0, waste_ready: 0 }
  );

  const sortedRecords = [...records].sort((a, b) => {
    const rank = (r: WasteRecord) => {
      const status = getRecordStatus(r);
      if (status === 'pfp_open') return 0;
      if (status === 'after_print_open') return 1;
      if (status === 'waste_ready') return 2;
      return 3;
    };
    return rank(a) - rank(b);
  });

  const totalWasteLbs = summaryRows.reduce((sum, row) => sum + Number(row.total_waste_lbs || 0), 0);
  const totalMeasuredRows = summaryRows.reduce((sum, row) => sum + Number(row.measured_records || 0), 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Waste Management System</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadRecords(true)}
            disabled={loading || syncing}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync Trello
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Cards are tracked from Bulk Management Trello. PFP In is editable at Color Check and Adjustment only.
        After moving to machine lists (R1, R4, R5, D1, D2, Sublimation), PFP In locks and After Print opens.
        After leaving machine lists, After Print locks.
      </Typography>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabIndex} onChange={(_: React.SyntheticEvent, v: number) => setTabIndex(v)}>
          <Tab label="All Data" />
          <Tab label="Waste Analysis" />
        </Tabs>
      </Paper>

      {tabIndex === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search"
                  placeholder="Card name, batch, or list"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SyncIcon fontSize="small" /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(e: SelectChangeEvent<StatusFilter>) => setStatusFilter(e.target.value as StatusFilter)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pfp_open">PFP In Open</MenuItem>
                    <MenuItem value="after_print_open">After Print Open</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="waste_ready">Waste Ready</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Current List"
                  value={listFilter}
                  onChange={(e) => setListFilter(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="From"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="To"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => loadRecords(false)} disabled={loading || syncing}>Apply Filters</Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setSearch('');
                      setStatusFilter('all');
                      setListFilter('');
                      setStartDate('');
                      setEndDate('');
                      setTimeout(() => loadRecords(false), 0);
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={2.4}><Card><CardContent><Typography color="text.secondary">Total</Typography><Typography variant="h5">{statusCounts.total}</Typography></CardContent></Card></Grid>
            <Grid item xs={12} sm={6} md={2.4}><Card><CardContent><Typography color="text.secondary">PFP In Open</Typography><Typography variant="h5">{statusCounts.pfp_open}</Typography></CardContent></Card></Grid>
            <Grid item xs={12} sm={6} md={2.4}><Card><CardContent><Typography color="text.secondary">After Print Open</Typography><Typography variant="h5">{statusCounts.after_print_open}</Typography></CardContent></Card></Grid>
            <Grid item xs={12} sm={6} md={2.4}><Card><CardContent><Typography color="text.secondary">Waste Ready</Typography><Typography variant="h5">{statusCounts.waste_ready}</Typography></CardContent></Card></Grid>
            <Grid item xs={12} sm={6} md={2.4}><Card><CardContent><Typography color="text.secondary">Completed</Typography><Typography variant="h5">{statusCounts.completed}</Typography></CardContent></Card></Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Card</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Current List</TableCell>
                  <TableCell align="right">Trello Weight (LBS)</TableCell>
                  <TableCell align="right">PFP In (LBS)</TableCell>
                  <TableCell align="right">After Print (LBS)</TableCell>
                  <TableCell align="right">Waste (LBS)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRecords.map((record) => {
                  const pfpEditable = canEdit && !record.locked_pfp_in;
                  const afterEditable = canEdit && !record.locked_after_print;

                  return (
                    <TableRow key={record.id}>
                      <TableCell>{getStatusChip(record)}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" fontWeight={600}>{record.trello_card_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.trello_card_id} | Updated: {new Date(record.updated_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.batch_number || '-'}</TableCell>
                      <TableCell>{record.current_list_name || '-'}</TableCell>
                      <TableCell align="right">{record.source_weight_lbs ?? '-'}</TableCell>

                      <TableCell align="right">
                        {pfpEditable ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <TextField
                              size="small"
                              type="number"
                              value={pfpDraft[record.id] ?? (record.pfp_in_weight_lbs ?? '')}
                              onChange={(e) => setPfpDraft({ ...pfpDraft, [record.id]: e.target.value })}
                              inputProps={{ min: 0, step: '0.01' }}
                              sx={{ width: 120 }}
                            />
                            <Button size="small" variant="contained" onClick={() => savePfp(record)}>Save</Button>
                          </Stack>
                        ) : (
                          <>{record.pfp_in_weight_lbs ?? '-'}</>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {afterEditable ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <TextField
                              size="small"
                              type="number"
                              value={afterPrintDraft[record.id] ?? (record.after_print_weight_lbs ?? '')}
                              onChange={(e) => setAfterPrintDraft({ ...afterPrintDraft, [record.id]: e.target.value })}
                              inputProps={{ min: 0, step: '0.01' }}
                              sx={{ width: 120 }}
                            />
                            <Button size="small" variant="contained" onClick={() => saveAfterPrint(record)}>Save</Button>
                          </Stack>
                        ) : (
                          <>{record.after_print_weight_lbs ?? '-'}</>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {record.waste_weight_lbs === null ? '-' : Number(record.waste_weight_lbs).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {records.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">No waste management records found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tabIndex === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group By</InputLabel>
                  <Select
                    label="Group By"
                    value={summaryPeriod}
                    onChange={(e) => setSummaryPeriod(e.target.value as 'date' | 'week' | 'month' | 'year')}
                  >
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="week">Week</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="year">Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={summaryStatus}
                    onChange={(e: SelectChangeEvent<StatusFilter>) => setSummaryStatus(e.target.value as StatusFilter)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pfp_open">PFP In Open</MenuItem>
                    <MenuItem value="after_print_open">After Print Open</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="waste_ready">Waste Ready</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="From"
                  type="date"
                  value={summaryStartDate}
                  onChange={(e) => setSummaryStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="To"
                  type="date"
                  value={summaryEndDate}
                  onChange={(e) => setSummaryEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={loadSummary} disabled={summaryLoading}>Apply</Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setSummaryPeriod('date');
                      setSummaryStatus('all');
                      setSummaryStartDate('');
                      setSummaryEndDate('');
                      setTimeout(() => loadSummary(), 0);
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}><Card><CardContent><Typography color="text.secondary">Buckets</Typography><Typography variant="h5">{summaryRows.length}</Typography></CardContent></Card></Grid>
            <Grid item xs={12} sm={6} md={4}><Card><CardContent><Typography color="text.secondary">Measured Entries</Typography><Typography variant="h5">{totalMeasuredRows}</Typography></CardContent></Card></Grid>
            <Grid item xs={12} sm={6} md={4}><Card><CardContent><Typography color="text.secondary">Total Waste (LBS)</Typography><Typography variant="h5">{totalWasteLbs.toFixed(2)}</Typography></CardContent></Card></Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{summaryPeriod === 'week' ? 'Week Start' : summaryPeriod === 'month' ? 'Month' : summaryPeriod === 'year' ? 'Year' : 'Date'}</TableCell>
                  <TableCell align="right">Total Records</TableCell>
                  <TableCell align="right">Measured Records</TableCell>
                  <TableCell align="right">Total Waste (LBS)</TableCell>
                  <TableCell align="right">Average Waste (LBS)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((row) => (
                  <TableRow key={row.bucket}>
                    <TableCell>{row.bucket}</TableCell>
                    <TableCell align="right">{row.total_records}</TableCell>
                    <TableCell align="right">{row.measured_records}</TableCell>
                    <TableCell align="right">{Number(row.total_waste_lbs || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{row.avg_waste_lbs === null ? '-' : Number(row.avg_waste_lbs).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {summaryRows.length === 0 && !summaryLoading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">No waste summary data found for selected filters</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {(loading || summaryLoading) && (
        <Box mt={2} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading records...' : 'Loading waste summary...'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WasteManagementSystem;
